import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { ApiService } from '@services/api/api.service';
import { ConfigCacheService } from '@services/api/config-cache.service';

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  constructor(
    private api: ApiService,
    private configCache: ConfigCacheService
  ) {}

  // GET API

  getTasksWithFilter(filter: any) {
    return this.api.get('/tasks?include=user,assignToUsers,assigns,taskStatus,jobType,property,taskAppraisal', filter);
  }
  bill(printType, category, qty) {
    return this.api.get('/task-categories/' + category + '/printing-type/' + printType + '/quantity/' + qty);
  }
  getJobTypeList() {
    return this.api.get('/job-types?office_id=' + this.api.getOfficeId());
  }
  getJobTypeById(id: string) {
    return this.api.get('/job-types/' + id);
  }
  resetTaskManager(payload: any) {
    return this.api.get('/taskmanager/reset', payload);
  }
  getStatuses(payload: any) {
    return this.api.get('/task-statuses', payload);
  }
  getTaskList(payload: any) {
    return this.api.get('/tasks?show=all&include=user,assignToUsers,assigns,taskStatus,jobType,property,taskAppraisal', payload);
  }
  getTypes(payload: any) {
    return this.api.get('/job-types', payload);
  }
  getPriorities(payload: any) {
    return this.api.get('/priorities', payload);
  }
  getTaskById(id: string, inc: string = '?include=assigns,assignToUsers,taskStatus,jobType,property,billings,attachments') {
    return this.api.get('/tasks/' + id + inc);
  }
  gUsers() {
    return this.api.get('/users/my-offices-users-list');
  }
  getLeadsTasks(id: string) {
    return this.api.get(`/leads/get/task/${id}`);
  }
  /**
   * `params` carries the consolidated-precinct scope (`project_ids`) when the user has switched
   * the project page to "Entire precinct"; it is omitted otherwise, so the default request is
   * unchanged. The id stays in the path either way — the server prefers `project_ids` when it is
   * present. See docs/backend/precinct/precinct-consolidated-view.md.
   */
  getProjectTasks(id: string, params: any = {}) {
    return this.api.get(`/get-project-tasks/${id}`, params);
  }
  taskOverview() {
    return this.api.get(`/tasks-overview/${this.api.getOfficeId()}/${this.api.getUserId()}`);
  }
  taskChangeAssignStatus(office_id) {
    return this.api.get('/tasks-get-all-by-status', { office_id: office_id });
  }
  getTaskCreatingUser() {
    return this.api.get('/contacts/getbyid/' + this.api.getUserId());
  }
  getTaskBoard(payload: any) {
    return this.api.get('/tasks-get-all-by-status-v1', payload);
  }
  markAsTaskRead(id: string) {
    return this.api.get(`/task-mark-as-readed/${id}`);
  }
  getTaskCheckList(id: string) {
    return this.api.get('/task/' + id + '/checklists');
  }
  getChildTaskDetail(id: string) {
    return this.api.get('/tasks-child-detail/' + id);
  }
  getProjectLot(id: string) {
    return this.api.get('/project/apartments/' + id);
  }
  getTaskComments(id: string) {
    return this.api.get('/comments/by-tasks/' + id + '?include=user');
  }
  duplicateTask(id: string, parent_id: any) {
    return this.api.get(`/duplicate-tasks/${id}?parentId=${parent_id}`);
  }
  getLeadDetailById(id: string) {
    return this.api.get(`/lead-detail-by-id/${id}`);
  }
  getTaskListByPropertyId(propertyId: string) {
    return this.api.get('/listing/property/task/' + propertyId);
  }
  getTaskByPropertyId(propertyId: string) {
    return this.api.get(`/property/task/${propertyId}`);
  }
  getPriorityById(id: any) {
    return this.api.get('/priorities/' + id);
  }
  getActivityLogList(id: string) {
    return this.api.get('/task/' + id + '/logs');
  }
  getBySearch(url) {
    return this.api.get(url);
  }
  // POST API

  addTask(payload: any) {
    return this.api.post('/tasks', payload);
  }
  deleteJobType(id: any) {
    return this.api.post('/job-types/' + id, { _method: 'DELETE' });
  }
  deletePriority(id: string) {
    return this.api.post('/priorities/' + id, { _method: 'DELETE' });
  }
  deleteTaskStatus(id: string) {
    return this.api.post('/task-statuses/' + id, { _method: 'DELETE' }).pipe(tap(() => this.configCache.evict('/task-statuses')));
  }
  createPriority(payload: any) {
    return this.api.post('/priorities', payload);
  }
  createJobType(payload: any) {
    return this.api.post('/job-types', payload);
  }
  taskChangeAssign(id, payload: any) {
    return this.api.post('/task/' + id + '/change-assignee', payload);
  }
  createTask(payload: any) {
    return this.api.post('/tasks', payload);
  }
  deleteBulkTask(ids: any) {
    return this.api.post('/tasks/bulk-delete', { taskIds: ids });
  }
  createTaskComments(payload: any) {
    return this.api.post('/comments', payload);
  }
  getTaskByOffice() {
    return this.api.get('/tasks?show=all&only=me&office_id=' + this.api.getOfficeId());
  }

  // PUT API

  changeTaskStatus(taskId: number, statusId: number) {
    return this.api.put('/tasks/' + taskId + '/task-status', { id: statusId });
  }
  updateTask(id, payload: any) {
    return this.api.put('/tasks/' + id, payload);
  }
  taskPriority(id: string, selectedPriorityBulk: any) {
    return this.api.put('/tasks/' + id + '/set-priority', { priority_id: selectedPriorityBulk });
  }
  updateJobType(id: string, payload: any) {
    return this.api.put('/job-types/' + id, payload);
  }
  updatePriority(id: any, payload: any) {
    return this.api.put('/priorities/' + id, payload);
  }
  // DELETE API

  deleteTaskLogo(id: string) {
    return this.api.delete('/tasklogo/' + id);
  }
  deleteTask(id: any) {
    return this.api.delete(`/tasks/${id}`);
  }
}
export interface ITaskFilter {
  dueDate: any;
}
